import React from 'react';

const Mietpreise: React.FC = () => {
  const rentalData = {
    topLagen: {
      name: 'Top-Lagen',
      districts: 'Altstadt, Westenviertel, Kumpfmühl, Galgenberg',
      angebotsmiete: { min: 13.50, max: 18.00 },
      bestandsmiete: { min: 9.50, max: 12.50 },
      notes: {
        angebot: 'Dies ist die realistische Spanne für neue Mietverträge.',
        bestand: 'Bestehende Verträge liegen oft bei ~9,50 € - 12,50 €.'
      }
    },
    guteLagen: {
      name: 'Gute Lagen',
      districts: 'Innerer Osten, Teile des Südostenviertels, Kasernenviertel',
      angebotsmiete: { min: 11.50, max: 15.00 },
      bestandsmiete: { min: 9.00, max: 11.50 },
      notes: {
        angebot: 'Solide, gefragte Wohngegenden.',
        bestand: 'Bestehende Verträge liegen oft bei ~9,00 € - 11,50 €.'
      }
    },
    einfacheLagen: {
      name: 'Einfache / Günstigere Lagen',
      districts: 'Konradsiedlung, Teile von Burgweinting & Ostenviertel',
      angebotsmiete: { min: 10.00, max: 13.00 },
      bestandsmiete: { min: 8.80, max: 10.50 },
      notes: {
        angebot: 'Geringere Nachfrage, daher moderatere Preise.',
        bestand: 'Bestehende Verträge liegen oft bei ~8,80 € - 10,50 €.'
      }
    }
  };

  const sizeFactors = [
    {
      size: 'Kleine Wohnungen (< 50 m²)',
      description: 'Tendieren zum oberen Ende der Preisspanne oder darüber.',
      type: 'small'
    },
    {
      size: 'Große Wohnungen (> 100 m²)', 
      description: 'Tendieren zum unteren Ende der Preisspanne.',
      type: 'large'
    }
  ];

  const buildingAge = [
    {
      age: 'Neubau (< 5 Jahre)',
      description: 'Befindet sich am absoluten oberen Ende der Spanne oder überschreitet diese.',
      type: 'new'
    },
    {
      age: 'Sanierter Altbau (hoher Standard)',
      description: 'Preislich oft auf dem Niveau von Neubauten.',
      type: 'renovated'
    },
    {
      age: 'Unsanierter Altbau (z.B. 70er-Jahre)',
      description: 'Befindet sich am unteren Ende der Spanne.',
      type: 'old'
    }
  ];

  // Calculate bar heights for the chart (scaled to max 180px height)
  const maxPrice = 20;
  const scaleHeight = (price: number) => (price / maxPrice) * 180;

  return (
    <div className="tab-content active space-y-8">
      <div className="bg-white p-6 sm:p-8 rounded-lg shadow">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Zusammenfassende Mietpreisübersicht für Regensburg
        </h2>
        <p className="text-gray-600 mb-6">
          Die folgende Übersicht kombiniert die Erkenntnisse aus dem offiziellen Mietspiegel 2023 
          (der die rechtliche Basis für bestehende Verträge darstellt) und den aktuellen 
          Angebotsmieten (2024/2025) von Immobilienportalen, die den realen Markt für 
          Wohnungssuchende widerspiegeln. Alle Preise sind Kaltmieten (Nettomieten ohne Nebenkosten) 
          in Euro pro Quadratmeter (€/m²).
        </p>

        {/* Bar Chart */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Grafik: Mietpreisspannen nach Lage
          </h3>
          <div className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
            <div className="min-w-[500px]">
              <svg viewBox="0 0 500 320" className="w-full h-80">
                {/* Legend */}
                <g className="legend">
                  <rect x="350" y="20" width="15" height="15" fill="#2563eb" />
                  <text x="370" y="32" fontSize="12" fill="currentColor">Angebotsmiete (Neuvertrag)</text>
                  <rect x="350" y="40" width="15" height="15" fill="#a5b4fc" />
                  <text x="370" y="52" fontSize="12" fill="currentColor">Bestandsmiete (Mietspiegel)</text>
                </g>

                {/* Y-axis labels */}
                <text x="25" y="65" textAnchor="end" fontSize="11" fill="currentColor">20€</text>
                <text x="25" y="100" textAnchor="end" fontSize="11" fill="currentColor">15€</text>
                <text x="25" y="135" textAnchor="end" fontSize="11" fill="currentColor">10€</text>
                <text x="25" y="170" textAnchor="end" fontSize="11" fill="currentColor">5€</text>
                <text x="25" y="205" textAnchor="end" fontSize="11" fill="currentColor">0€</text>

                {/* Grid lines */}
                <line x1="30" y1="60" x2="320" y2="60" stroke="#e5e7eb" strokeDasharray="2,2" />
                <line x1="30" y1="95" x2="320" y2="95" stroke="#e5e7eb" strokeDasharray="2,2" />
                <line x1="30" y1="130" x2="320" y2="130" stroke="#e5e7eb" strokeDasharray="2,2" />
                <line x1="30" y1="165" x2="320" y2="165" stroke="#e5e7eb" strokeDasharray="2,2" />
                <line x1="30" y1="200" x2="320" y2="200" stroke="#9ca3af" />

                {/* Top-Lagen bars */}
                <g className="chart-bar">
                  <rect 
                    x="60" 
                    y={200 - scaleHeight(rentalData.topLagen.bestandsmiete.max)} 
                    width="50" 
                    height={scaleHeight(rentalData.topLagen.bestandsmiete.max - rentalData.topLagen.bestandsmiete.min)}
                    fill="#a5b4fc"
                  >
                    <title>Bestand Top-Lage: {rentalData.topLagen.bestandsmiete.min}€ - {rentalData.topLagen.bestandsmiete.max}€</title>
                  </rect>
                  <rect 
                    x="60" 
                    y={200 - scaleHeight(rentalData.topLagen.angebotsmiete.max)} 
                    width="50" 
                    height={scaleHeight(rentalData.topLagen.angebotsmiete.max - rentalData.topLagen.angebotsmiete.min)}
                    fill="#2563eb"
                  >
                    <title>Angebot Top-Lage: {rentalData.topLagen.angebotsmiete.min}€ - {rentalData.topLagen.angebotsmiete.max}€</title>
                  </rect>
                  <text x="85" y="220" textAnchor="middle" fontSize="12" fontWeight="bold" fill="currentColor">
                    Top-Lagen
                  </text>
                </g>

                {/* Gute Lagen bars */}
                <g className="chart-bar">
                  <rect 
                    x="150" 
                    y={200 - scaleHeight(rentalData.guteLagen.bestandsmiete.max)} 
                    width="50" 
                    height={scaleHeight(rentalData.guteLagen.bestandsmiete.max - rentalData.guteLagen.bestandsmiete.min)}
                    fill="#a5b4fc"
                  >
                    <title>Bestand Gute Lage: {rentalData.guteLagen.bestandsmiete.min}€ - {rentalData.guteLagen.bestandsmiete.max}€</title>
                  </rect>
                  <rect 
                    x="150" 
                    y={200 - scaleHeight(rentalData.guteLagen.angebotsmiete.max)} 
                    width="50" 
                    height={scaleHeight(rentalData.guteLagen.angebotsmiete.max - rentalData.guteLagen.angebotsmiete.min)}
                    fill="#2563eb"
                  >
                    <title>Angebot Gute Lage: {rentalData.guteLagen.angebotsmiete.min}€ - {rentalData.guteLagen.angebotsmiete.max}€</title>
                  </rect>
                  <text x="175" y="220" textAnchor="middle" fontSize="12" fontWeight="bold" fill="currentColor">
                    Gute Lagen
                  </text>
                </g>

                {/* Einfache Lagen bars */}
                <g className="chart-bar">
                  <rect 
                    x="240" 
                    y={200 - scaleHeight(rentalData.einfacheLagen.bestandsmiete.max)} 
                    width="50" 
                    height={scaleHeight(rentalData.einfacheLagen.bestandsmiete.max - rentalData.einfacheLagen.bestandsmiete.min)}
                    fill="#a5b4fc"
                  >
                    <title>Bestand Einfache Lage: {rentalData.einfacheLagen.bestandsmiete.min}€ - {rentalData.einfacheLagen.bestandsmiete.max}€</title>
                  </rect>
                  <rect 
                    x="240" 
                    y={200 - scaleHeight(rentalData.einfacheLagen.angebotsmiete.max)} 
                    width="50" 
                    height={scaleHeight(rentalData.einfacheLagen.angebotsmiete.max - rentalData.einfacheLagen.angebotsmiete.min)}
                    fill="#2563eb"
                  >
                    <title>Angebot Einfache Lage: {rentalData.einfacheLagen.angebotsmiete.min}€ - {rentalData.einfacheLagen.angebotsmiete.max}€</title>
                  </rect>
                  <text x="265" y="220" textAnchor="middle" fontSize="12" fontWeight="bold" fill="currentColor">
                    Einfache Lagen
                  </text>
                </g>
              </svg>
            </div>
          </div>
        </div>

        {/* Table with rental price details */}
        <div className="overflow-x-auto mb-8">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lage / Stadtteil
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Typische Preisspanne pro m² (Kalt)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Anmerkungen (Einfluss von Größe & Baujahr)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Object.values(rentalData).map((area, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-semibold">{area.name}</div>
                    <div className="text-sm text-gray-600">{area.districts}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-800">
                    {area.angebotsmiete.min.toFixed(2)} € - {area.angebotsmiete.max.toFixed(2)} €
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    <span className="font-semibold">Angebotsmiete:</span> {area.notes.angebot}<br/>
                    <span className="font-semibold">Bestandsmiete:</span> {area.notes.bestand}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Details by size and building age */}
        <div className="space-y-8">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Detaillierung nach Größe und Baujahr (für Angebotsmieten)
            </h3>
            <p className="text-gray-600 mb-6">
              Innerhalb der oben genannten Preisspannen gelten folgende Regeln, um den Preis genauer einzuschätzen:
            </p>
          </div>

          {/* Two column layout for factors */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Size factors */}
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-4 uppercase tracking-wider text-gray-500">
                Faktor: Wohnungsgröße
              </h4>
              <div className="space-y-4">
                {sizeFactors.map((factor, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="font-semibold text-gray-800 mb-1">
                      {factor.size}:
                    </div>
                    <div className="text-sm text-gray-700">
                      {factor.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Building age factors */}
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-4 uppercase tracking-wider text-gray-500">
                Einfluss auf den Preis pro m² innerhalb der Spanne
              </h4>
              <div className="space-y-4">
                {buildingAge.map((age, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="font-semibold text-gray-800 mb-1">
                      {age.age}:
                    </div>
                    <div className="text-sm text-gray-700">
                      {age.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Summary note */}
          <div className="mt-8 p-6 bg-blue-50 border-l-4 border-blue-400 rounded-lg">
            <div className="flex">
              <div className="ml-3">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">
                  Zusammenfassend lässt sich sagen:
                </h4>
                <p className="text-sm text-blue-700">
                  Eine kleine, moderne Wohnung in einer Top-Lage wird sehr wahrscheinlich über 18,00 €/m² kosten, 
                  während eine große, unsanierte Wohnung in einer einfachen Lage eher bei 10,00 €/m² zu finden ist. 
                  Diese Tabelle dient als eine verlässliche Grundlage für Ihre Einschätzung des Regensburger Mietmarktes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Mietpreise;